const chapter = {
  id: 'current-electricity',
  slug: 'current-electricity',
  subject: 'physics',
  subjectLabel: 'Physics',
  exam: 'JEE Main + NEET',
  title: {
    english: 'Current Electricity',
        hindi: 'विद्युत धारा',
  },
  estReadMinutes: 65,
  difficulty: {
    english: 'Medium',
        hindi: 'मध्यम',
  },
  weightage: {
    english: '3–4 questions in JEE Main / NEET',
        hindi: 'जेईई मेन/नीट में 3-4 प्रश्न',
  },
  syllabusTags: ['Electromagnetism', 'JEE-Main', 'NEET', 'Class-12'],
  sections: [
    {
      id: 'basics',
      title: {
        english: 'Current, Drift Velocity & Ohm\'s Law',
        hindi: 'धारा, अपवाह वेग और ओम का नियम',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'Electric current',
        hindi: 'विद्युत धारा',
          },
          body: {
            english: '$I = dq/dt$. SI unit: ampere (A). Direction = direction of conventional (positive charge) flow.',
        hindi: '$I = dq/dt$. एसआई इकाई: एम्पीयर (ए)। दिशा = पारंपरिक (धनात्मक आवेश) प्रवाह की दिशा।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Drift velocity',
        hindi: 'बहाव वेग',
          },
          statement: {
            english: 'Electrons in conductor under field $E$ acquire average drift speed $v_d = -\\dfrac{eE\\tau}{m}$, $\\tau$ = relaxation time. Current density $\\vec j = -ne\\vec v_d$, magnitude $j = nev_d$.',
        hindi: 'फ़ील्ड $E$ के अंतर्गत कंडक्टर में इलेक्ट्रॉन औसत बहाव गति $v_d = -\\dfrac{eE\\tau}{m}$, $\\tau$ = विश्राम समय प्राप्त करते हैं। वर्तमान घनत्व $\\vec j = -ne\\vec v_d$, परिमाण $j = nev_d$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Microscopic Ohm\'s law: $\\vec j = \\sigma \\vec E$, $\\sigma = ne^2\\tau/m$ = conductivity.',
        hindi: 'सूक्ष्मदर्शी ओम का नियम: $\\vec j = \\sigma \\vec E$, $\\sigma = ne^2\\tau/m$ = चालकता।',
            },
            {
              english: 'Macroscopic Ohm\'s law: $V = IR$.',
        hindi: 'मैक्रोस्कोपिक ओम का नियम: $V = IR$। **प्रतिरोध** $V = IR$, = प्रतिरोधकता (Ω·m)। .',
            },
            {
              english: '**Resistance** $R = \\rho L/A$, $\\rho$ = resistivity (Ω·m). $\\rho = m/(ne^2\\tau)$.',
        hindi: '**प्रतिरोध** $R = \\rho L/A$, $\\rho$ = प्रतिरोधकता (Ω·m)। $\\rho = m/(ne^2\\tau)$.',
            },
            {
              english: 'Mobility $\\mu = v_d/E = e\\tau/m$.',
        hindi: 'गतिशीलता $\\mu = v_d/E = e\\tau/m$.',
            },
            {
              english: 'Temperature: $\\rho_T = \\rho_0 [1 + \\alpha(T - T_0)]$. $\\alpha > 0$ for metals, $\\alpha < 0$ for semiconductors.',
        hindi: 'तापमान: $\\rho_T = \\rho_0 [1 + \\alpha(T - T_0)]$. धातुओं के लिए $\\alpha > 0$, अर्धचालकों के लिए $\\alpha < 0$।',
            },
          ],
        },
      ],
    },
    {
      id: 'combinations',
      title: {
        english: 'Resistors — Series, Parallel & Color Code',
        hindi: 'प्रतिरोधक — श्रेणी, समान्तर और रंग कूट',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: '**Series**: $R_{\\text{eq}} = \\sum R_i$. Same current.',
        hindi: '**श्रृंखला**: $R_{\\text{eq}} = \\sum R_i$. वही धारा.',
            },
            {
              english: '**Parallel**: $1/R_{\\text{eq}} = \\sum 1/R_i$. Same voltage.',
        hindi: '**समानांतर**: $1/R_{\\text{eq}} = \\sum 1/R_i$. वही वोल्टेज.',
            },
            {
              english: '**Color code** (4-band): first 2 bands = digits, 3rd = multiplier (×$10^n$), 4th = tolerance (gold ±5%, silver ±10%, none ±20%). Mnemonic: **B B ROY of Great Britain has Very Good Wife** → Black 0, Brown 1, Red 2, Orange 3, Yellow 4, Green 5, Blue 6, Violet 7, Grey 8, White 9.',
        hindi: '**रंग कोड** (4-बैंड): पहले 2 bands = digits, तीसरा = गुणक (×$10^n$), चौथा = सहनशीलता (सोना ±5%, चांदी ±10%, कोई नहीं ±20%)। स्मरणीय: ** ग्रेट ब्रिटेन के बी बी रॉय की पत्नी बहुत अच्छी है** → काला 0, भूरा 1, लाल 2, नारंगी 3, पीला 4, हरा 5, नीला 6, बैंगनी 7, ग्रे 8, सफेद 9।',
            },
          ],
        },
      ],
    },
    {
      id: 'kirchhoff',
      title: {
        english: 'EMF, Internal Resistance & Kirchhoff\'s Laws',
        hindi: 'EMF, आन्तरिक प्रतिरोध और किरचॉफ के नियम',
      },
      blocks: [
        {
          type: 'definition',
          term: {
            english: 'EMF & terminal voltage',
        hindi: 'ईएमएफ और टर्मिनल वोल्टेज',
          },
          body: {
            english: 'EMF $\\varepsilon$ = work done by source per unit charge in driving current. Cell with internal resistance $r$, current $I$: terminal voltage $V = \\varepsilon - Ir$ (discharging), $V = \\varepsilon + Ir$ (charging).',
        hindi: 'ईएमएफ $\\varepsilon$ = ड्राइविंग करंट में प्रति यूनिट चार्ज स्रोत द्वारा किया गया कार्य। आंतरिक प्रतिरोध $r$, वर्तमान $I$ वाला सेल: टर्मिनल वोल्टेज $V = \\varepsilon - Ir$ (डिस्चार्जिंग), $V = \\varepsilon + Ir$ (चार्जिंग)।',
          },
        },
        {
          type: 'theorem',
          name: {
            english: 'Kirchhoff\'s laws',
        hindi: 'किरचॉफ के नियम',
          },
          statement: {
            english: `**Junction (KCL)**: $\\sum I_{\\text{in}} = \\sum I_{\\text{out}}$ at any node (charge conservation).
**Loop (KVL)**: $\\sum \\varepsilon = \\sum IR$ around any closed loop (energy conservation).`,
            hindi: '**जंक्शन (KCL)**: किसी भी नोड पर $\\sum I_{\\text{in}} = \\sum I_{\\text{out}}$ (चार्ज संरक्षण)। **लूप (KVL)**: किसी भी बंद लूप (ऊर्जा संरक्षण) के आसपास $\\sum \\varepsilon = \\sum IR$।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: 'Cells in series: $\\varepsilon_{\\text{eq}} = \\sum \\varepsilon_i$, $r_{\\text{eq}} = \\sum r_i$.',
        hindi: 'श्रृंखला में कोशिकाएँ: $\\varepsilon_{\\text{eq}} = \\sum \\varepsilon_i$, $r_{\\text{eq}} = \\sum r_i$।',
            },
            {
              english: 'Identical cells in parallel: $\\varepsilon_{\\text{eq}} = \\varepsilon$, $r_{\\text{eq}} = r/n$.',
        hindi: 'समानांतर में समान कोशिकाएँ: $\\varepsilon_{\\text{eq}} = \\varepsilon$, $r_{\\text{eq}} = r/n$।',
            },
            {
              english: '**Mixed grouping** (m series in n parallel, $N = mn$ cells): for max current to load $R_L$, set $r_{\\text{int}}\' = mr/n = R_L$.',
        hindi: '**मिश्रित समूहन** (एन समानांतर में एम श्रृंखला, $N = mn$ सेल): $R_L$ लोड करने के लिए अधिकतम करंट के लिए, $r_{\\text{int}}\\\' = mr/n = R_L$ सेट करें।',
            },
          ],
        },
        {
          type: 'example',
          number: 1,
          problem: {
            english: 'Cell of EMF 12 V, internal resistance 2 Ω, connected to 4 Ω. Current and terminal voltage?',
        hindi: 'EMF 12 V का सेल, आंतरिक प्रतिरोध 2 Ω, 4 Ω से जुड़ा है। वर्तमान और टर्मिनल वोल्टेज?',
          },
          solution: {
            english: '$I = 12/(2+4) = 2$ A. $V = 12 - 2\\cdot 2 = 8$ V.',
        hindi: '$I = 12/(2+4) = 2$ A. $V = 12 - 2\\cdot 2 = 8$ V.',
          },
        },
      ],
    },
    {
      id: 'wheatstone',
      title: {
        english: 'Wheatstone Bridge & Meter Bridge',
        hindi: 'व्हीटस्टोन सेतु और मीटर सेतु',
      },
      blocks: [
        {
          type: 'theorem',
          name: {
            english: 'Wheatstone bridge balance',
        hindi: 'व्हीटस्टोन ब्रिज संतुलन',
          },
          statement: {
            english: `For four resistors $P, Q, R, S$ in bridge with galvanometer between B–D and source across A–C:
$$\\dfrac{P}{Q} = \\dfrac{R}{S} \\Leftrightarrow I_g = 0.$$
Most sensitive when all four resistors are of comparable order.`,
            hindi: 'बी-डी और ए-सी के पार स्रोत के बीच गैल्वेनोमीटर के साथ पुल में चार प्रतिरोधकों $P, Q, R, S$ के लिए: $$\\dfrac{P}{Q} = \\dfrac{R}{S} \\Leftrightarrow I_g = 0.$$ सबसे संवेदनशील तब होता है जब सभी चार प्रतिरोधक तुलनीय क्रम के हों।',
          },
        },
        {
          type: 'list',
          items: [
            {
              english: '**Meter bridge** (slide-wire form): $R = S \\cdot \\ell/(100 - \\ell)$ at balance length $\\ell$ cm.',
        hindi: '**मीटर ब्रिज** (स्लाइड-वायर फॉर्म): $R = S \\cdot \\ell/(100 - \\ell)$ बैलेंस लंबाई पर $\\ell$ सेमी।',
            },
            {
              english: '**Potentiometer**: zero-deflection method to compare EMFs / measure internal resistance. EMF ratio = balancing-length ratio.',
        hindi: '**पोटेंशियोमीटर**: ईएमएफ की तुलना करने/आंतरिक प्रतिरोध को मापने के लिए शून्य-विक्षेपण विधि। ईएमएफ ratio = balancing-length ratio.',
            },
            {
              english: 'Potentiometer is more accurate than voltmeter (no current drawn at balance).',
        hindi: 'पोटेंशियोमीटर वोल्टमीटर (संतुलन पर कोई विद्युत प्रवाह नहीं) की तुलना में अधिक सटीक है।',
            },
          ],
        },
      ],
    },
    {
      id: 'power-meters',
      title: {
        english: 'Electrical Power & Meters',
        hindi: 'विद्युत शक्ति और मीटर',
      },
      blocks: [
        {
          type: 'list',
          items: [
            {
              english: 'Power dissipated in resistor: $P = VI = I^2 R = V^2/R$. SI unit: watt.',
        hindi: 'अवरोधक में शक्ति का क्षय: $P = VI = I^2 R = V^2/R$। एसआई इकाई: वाट.',
            },
            {
              english: 'Energy: 1 kWh = $3.6\\times 10^6$ J.',
        hindi: 'ऊर्जा: 1 kWh = $3.6\\times 10^6$ J.',
            },
            {
              english: 'For resistors in **series** carrying same $I$: $P = I^2 R$ → larger $R$ dissipates more.',
        hindi: '**श्रृंखला** में समान $I$ वाले प्रतिरोधकों के लिए: $P = I^2 R$ → बड़ा $R$ अधिक क्षय करता है।',
            },
            {
              english: 'For resistors in **parallel** at same $V$: $P = V^2/R$ → smaller $R$ dissipates more.',
        hindi: '**समानांतर** में समान $V$ पर प्रतिरोधकों के लिए: $P = V^2/R$ → छोटा $R$ अधिक क्षय करता है।',
            },
            {
              english: '**Galvanometer to ammeter** (low $R$ shunt): $S = G I_g/(I - I_g)$.',
        hindi: '**गैल्वेनोमीटर से एमीटर** (कम $R$ शंट): $S = G I_g/(I - I_g)$।',
            },
            {
              english: '**Galvanometer to voltmeter** (high $R$ in series): $R = V/I_g - G$.',
        hindi: '**गैल्वेनोमीटर से वोल्टमीटर** (श्रृंखला में उच्च $R$): $R = V/I_g - G$।',
            },
          ],
        },
        {
          type: 'example',
          number: 2,
          problem: {
            english: 'Three identical bulbs in (a) series and (b) parallel across same supply. Ratio of powers?',
        hindi: 'एक ही आपूर्ति में (ए) श्रृंखला और (बी) समानांतर में तीन समान बल्ब। शक्तियों का अनुपात?',
          },
          solution: {
            english: 'Series: each bulb sees $V/3$, $P_s = V^2/(9R)$ each, total $V^2/(3R)$. Parallel: each $V$, $P_p = V^2/R$ each, total $3V^2/R$. Ratio total $= 1 : 9$.',
        hindi: 'श्रृंखला: प्रत्येक बल्ब $V/3$, $P_s = V^2/(9R)$ प्रत्येक, कुल $V^2/(3R)$ देखता है। समानांतर: प्रत्येक $V$, $P_p = V^2/R$ प्रत्येक, कुल $3V^2/R$। कुल अनुपात $= 1 : 9$.',
          },
        },
      ],
    },
  ],
  keyFormulas: [
    {
      latex: 'I = nAev_d',
      desc: {
        english: 'Drift current',
        hindi: 'बहाव धारा',
      },
    },
    {
      latex: 'R = \\rho L/A',
      desc: {
        english: 'Resistance',
        hindi: 'प्रतिरोध',
      },
    },
    {
      latex: 'V = IR,\\ V = \\varepsilon - Ir',
      desc: {
        english: 'Ohm + cell',
        hindi: 'ओम + सेल',
      },
    },
    {
      latex: 'P/Q = R/S',
      desc: {
        english: 'Wheatstone',
        hindi: 'व्हीटस्टोन',
      },
    },
    {
      latex: 'P = VI = I^2R = V^2/R',
      desc: {
        english: 'Power',
        hindi: 'शक्ति',
      },
    },
    {
      latex: 'S = GI_g/(I - I_g)',
      desc: {
        english: 'Ammeter shunt',
        hindi: 'एमीटर शंट',
      },
    },
    {
      latex: '\\rho_T = \\rho_0(1 + \\alpha\\Delta T)',
      desc: {
        english: 'Temp dependence',
        hindi: 'तापमान पर निर्भरता',
      },
    },
  ],
  pyqs: [
    {
      year: 2024,
      exam: 'JEE Main',
      tag: 'Kirchhoff',
      problem: {
        english: 'Two cells of EMF 4 V and 6 V (internal resistances 1 Ω and 2 Ω) connected in parallel to external 2 Ω. Current through external?',
        hindi: 'ईएमएफ 4 वी और 6 वी (आंतरिक प्रतिरोध 1 Ω और 2 Ω) की दो कोशिकाएं बाहरी 2 Ω के समानांतर जुड़ी हुई हैं। बाहरी माध्यम से करंट?',
      },
      answer: {
        english: '$\\approx 1.6$ A',
        hindi: '$\\approx 1.6$ ए',
      },
      solution: {
        english: 'Equivalent EMF $= (4/1 + 6/2)/(1/1 + 1/2) = (4+3)/(1.5) = 14/3$ V. $r_{eq} = 2/3$. $I = (14/3)/(2/3 + 2) = (14/3)/(8/3) = 14/8 = 1.75$ A. (Approx 1.75)',
        hindi: 'समतुल्य ईएमएफ $= (4/1 + 6/2)/(1/1 + 1/2) = (4+3)/(1.5) = 14/3$ वी. $r_{eq} = 2/3$. $I = (14/3)/(2/3 + 2) = (14/3)/(8/3) = 14/8 = 1.75$ A. (लगभग 1.75)',
      },
    },
    {
      year: 2023,
      exam: 'NEET',
      tag: 'Wheatstone',
      problem: {
        english: 'In a meter bridge, resistance 4 Ω in left gap balances at 40 cm. Unknown $R$ in right gap?',
        hindi: 'एक मीटर ब्रिज में, बाएं गैप में प्रतिरोध 4 Ω 40 सेमी पर संतुलित होता है। अज्ञात $R$ सही अंतराल में?',
      },
      answer: {
        english: '$6\\ \\Omega$',
        hindi: '$6\\\\ \\Omega$',
      },
      solution: {
        english: '$4/(R) = 40/60 \\Rightarrow R = 6$.',
        hindi: '$4/(आर) = 40/60 \\Rightarrow आर = 6$।',
      },
    },
    {
      year: 2022,
      exam: 'JEE Main',
      tag: 'Power',
      problem: {
        english: 'A 100 W, 220 V bulb operated at 110 V. Power consumed?',
        hindi: 'एक 100 W, 220 V बल्ब 110 V पर संचालित होता है। बिजली की खपत?',
      },
      answer: {
        english: '$25$ W',
        hindi: '$25$ डब्ल्यू',
      },
      solution: {
        english: '$R$ same; $P\' = V\'^2/R = (110)^2/(220^2/100) = 25$ W.',
        hindi: '$र$ वही; $P\' = V\'^2/R = (110)^2/(220^2/100) = 25$ W.',
      },
    },
    {
      year: 2021,
      exam: 'JEE Main',
      tag: 'Combinations',
      problem: {
        english: 'Equivalent resistance between A and B if 12 identical resistors $r$ form a cube (across body diagonal).',
        hindi: 'A और B के बीच समतुल्य प्रतिरोध यदि 12 समान प्रतिरोधक $r$ एक घन बनाते हैं (शरीर के विकर्ण पर)।',
      },
      answer: {
        english: '$5r/6$',
        hindi: '$5r/6$',
      },
      solution: {
        english: 'Standard cube result for body diagonal.',
        hindi: 'शरीर के विकर्ण के लिए मानक घन परिणाम।',
      },
    },
    {
      year: 2020,
      exam: 'NEET',
      tag: 'Drift velocity',
      problem: {
        english: 'Drift speed of electrons in copper wire ($n = 8.5\\times 10^{28}$/m³, area $10^{-6}$ m²) carrying 1 A.',
        hindi: '1 ए ले जाने वाले तांबे के तार (/m³, क्षेत्रफल $10^{-6}$ m²) में इलेक्ट्रॉनों की बहाव गति।',
      },
      answer: {
        english: '$\\approx 7.4\\times 10^{-5}$ m/s',
        hindi: '$\\approx 7.4\\times 10^{-5}$ मी/से',
      },
      solution: {
        english: '$v_d = I/(nAe) = 1/(8.5{\\times}10^{28}\\cdot 10^{-6}\\cdot 1.6{\\times}10^{-19})$.',
        hindi: '$v_d = I/(nAe) = 1/(8.5{\\times}10^{28}\\cdot 10^{-6}\\cdot 1.6{\\times}10^{-19})$.',
      },
    },
  ],
  next: {
    slug: 'moving-charges-magnetism',
    title: 'Moving Charges & Magnetism',
  },
};

export default chapter;
